/**
 * Generic utility to convert nested Record structures to flat arrays
 */

export interface NestedRecordToArrayConfig {
    /** Custom key names for each level of nesting */
    levelKeys?: string[];
    /** Function to transform the final value object */
    valueTransformer?: (value: any, context: { [key: string]: string }) => any;
    /** Whether to include null values for missing levels */
    includeNullForMissingLevels?: boolean;
    /** Filter function to exclude certain paths */
    pathFilter?: (path: string[]) => boolean;
}

/**
 * Converts a nested Record structure to a flat array
 * @param nestedRecord - The nested Record to convert
 * @param config - Configuration options
 * @returns Array of flattened objects
 */
export const convertNestedRecordToArray = (
    nestedRecord: Record<string, any>,
    config: NestedRecordToArrayConfig = {},
): Array<Record<string, any>> => {
    const { levelKeys = [], valueTransformer, includeNullForMissingLevels = true, pathFilter } = config;

    const result: Array<Record<string, any>> = [];

    const traverse = (obj: any, currentPath: string[] = [], currentLevel = 0) => {
        if (obj === null || obj === undefined) {
            return;
        }

        // If this is the final value (not an object or is an object with non-object values)
        if (typeof obj !== "object" || Array.isArray(obj) || isValueObject(obj)) {
            // Apply path filter if provided
            if (pathFilter && !pathFilter(currentPath)) {
                return;
            }

            // Create the result object with path information
            const resultItem: Record<string, any> = {};

            // Add level keys
            currentPath.forEach((pathValue, index) => {
                const keyName = levelKeys[index] || `level${index}`;
                resultItem[keyName] = includeNullForMissingLevels || pathValue !== "-" ? pathValue : null;
            });

            // Add the value (either directly or transformed)
            if (valueTransformer) {
                const context = currentPath.reduce((acc, pathValue, index) => {
                    const keyName = levelKeys[index] || `level${index}`;
                    acc[keyName] = pathValue;
                    return acc;
                }, {} as { [key: string]: string });

                const transformedValue = valueTransformer(obj, context);
                Object.assign(resultItem, transformedValue);
            } else {
                // If it's a value object, spread its properties
                if (typeof obj === "object" && !Array.isArray(obj)) {
                    Object.assign(resultItem, obj);
                } else {
                    resultItem.value = obj;
                }
            }

            result.push(resultItem);
            return;
        }

        // Continue traversing if it's still a nested object
        Object.entries(obj).forEach(([key, value]) => {
            traverse(value, [...currentPath, key], currentLevel + 1);
        });
    };

    traverse(nestedRecord);
    return result;
};

/**
 * Helper function to determine if an object is a "value object"
 * (i.e., contains data properties rather than nested structure)
 */
const isValueObject = (obj: any): boolean => {
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
        return false;
    }

    // Check if all values are primitive types or arrays
    return Object.values(obj).every(
        (value) =>
            value === null ||
            value === undefined ||
            typeof value === "string" ||
            typeof value === "number" ||
            typeof value === "boolean" ||
            Array.isArray(value),
    );
};

/**
 * Specialized function for 3-level nested Record conversion
 * Works with Record<string, Record<string, Record<string, any>>> structure
 */
export const convertTripleLevelRecordToArray = (
    nestedRecord: Record<string, Record<string, Record<string, any>>>,
    levelKeys: [string, string, string] = ["level1", "level2", "level3"],
    options: {
        hasBreakdown?: boolean;
        breakdownLevel?: number; // Which level represents breakdown (0, 1, or 2)
    } = {},
) => {
    const { hasBreakdown = false, breakdownLevel = 0 } = options;

    return convertNestedRecordToArray(nestedRecord, {
        levelKeys,
        valueTransformer: (value, context) => {
            const result: Record<string, any> = {
                // Add level context
                ...context,
                // Spread all properties from the final value object
                ...value,
            };

            // Handle breakdown logic
            if (!hasBreakdown && levelKeys[breakdownLevel]) {
                const breakdownKey = levelKeys[breakdownLevel];
                if (result[breakdownKey] === "-") {
                    result[breakdownKey] = null;
                }
            }

            return result;
        },
    });
};

/**
 * Convenience function for significance result conversion
 */
export const convertSignificanceResultToArray = (
    significanceResult: Record<string, Record<string, Record<string, any>>>,
    hasBreakdown = false,
) => {
    return convertTripleLevelRecordToArray(significanceResult, ["breakdownValue", "metric", "variantGroup"], {
        hasBreakdown,
        breakdownLevel: 0,
    });
};

type NestedRecord = Record<string, any>;

export function flattenNestedRecord(data: NestedRecord, levelKeyNames: string[]): Record<string, any>[] {
    const result: Record<string, any>[] = [];

    function traverse(
        node: NestedRecord,
        pathKeys: string[], // lưu tên key tại từng cấp
    ) {
        // Handle null/undefined nodes
        if (!node || typeof node !== "object" || Array.isArray(node)) {
            return;
        }

        const isLeaf = Object.values(node).every(
            (v) => v === null || v === undefined || typeof v !== "object" || Array.isArray(v),
        );

        if (isLeaf) {
            const record: Record<string, any> = {};
            // Map path keys to level names
            pathKeys.forEach((keyName, index) => {
                if (index < levelKeyNames.length) {
                    record[levelKeyNames[index]] = keyName;
                }
            });
            // Copy all leaf values
            Object.assign(record, node);
            result.push(record);
            return;
        }

        // Continue traversing nested objects
        for (const key of Object.keys(node)) {
            if (node[key] && typeof node[key] === "object" && !Array.isArray(node[key])) {
                traverse(node[key], [...pathKeys, key]);
            }
        }
    }

    traverse(data, []);
    return result;
}
